import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import AutomationFlowDirectoriesAPI from '../../api/automationFlowDirectories';
import { throwErrorMessage } from '../utils/api';

// RECUSA DE CONFIRMAÇÃO (#1336): o servidor responde 422 com `flows_to_hide`
// quando a mudança esconderia da equipe fluxos globais. O `throwErrorMessage`
// padrão lança um `Error` só com a MENSAGEM, e a lista se perderia — é
// justamente ela que a tela precisa mostrar antes de reenviar confirmado.
//
// Só este módulo trata o caso, e de propósito: reescrever o `throwErrorMessage`
// mudaria o contrato de erro de todas as stores.
const throwHiddenFlowsRefusal = error => {
  const data = error?.response?.data;
  if (!data?.flows_to_hide) return false;

  const refusal = new Error(data.error);
  refusal.flowsToHide = data.flows_to_hide;
  refusal.flowsToHideCount = data.flows_to_hide_count;
  throw refusal;
};

// Store das pastas de fluxos (#1071). Lista curta e sem paginação — ver o
// client.
export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getAutomationFlowDirectories(_state) {
    return _state.records;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_AUTOMATION_FLOW_DIRECTORY_UI_FLAG, { isFetching: true });
    try {
      const response = await AutomationFlowDirectoriesAPI.get();
      commit(
        types.SET_AUTOMATION_FLOW_DIRECTORIES,
        response.data.payload || []
      );
    } catch (error) {
      // A tela de fluxos abre sem pastas em vez de não abrir: durante a janela
      // do hook PostSync o backend ainda responde lista vazia, e um erro aqui
      // não pode derrubar a listagem, que é o assunto principal da tela.
      commit(types.SET_AUTOMATION_FLOW_DIRECTORIES, []);
    } finally {
      commit(types.SET_AUTOMATION_FLOW_DIRECTORY_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  create: async ({ commit }, payload) => {
    commit(types.SET_AUTOMATION_FLOW_DIRECTORY_UI_FLAG, { isCreating: true });
    try {
      const response = await AutomationFlowDirectoriesAPI.create(payload);
      commit(types.ADD_AUTOMATION_FLOW_DIRECTORY, response.data);
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_AUTOMATION_FLOW_DIRECTORY_UI_FLAG, {
        isCreating: false,
      });
    }
  },
  update: async ({ commit }, { id, ...payload }) => {
    commit(types.SET_AUTOMATION_FLOW_DIRECTORY_UI_FLAG, { isUpdating: true });
    try {
      const response = await AutomationFlowDirectoriesAPI.update(id, payload);
      commit(types.EDIT_AUTOMATION_FLOW_DIRECTORY, response.data);
      return response.data;
    } catch (error) {
      throwHiddenFlowsRefusal(error);
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_AUTOMATION_FLOW_DIRECTORY_UI_FLAG, {
        isUpdating: false,
      });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_AUTOMATION_FLOW_DIRECTORY_UI_FLAG, { isDeleting: true });
    try {
      await AutomationFlowDirectoriesAPI.delete(id);
      commit(types.DELETE_AUTOMATION_FLOW_DIRECTORY, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AUTOMATION_FLOW_DIRECTORY_UI_FLAG, {
        isDeleting: false,
      });
    }
  },
};

export const mutations = {
  [types.SET_AUTOMATION_FLOW_DIRECTORY_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.SET_AUTOMATION_FLOW_DIRECTORIES](_state, data) {
    _state.records = data || [];
  },
  [types.ADD_AUTOMATION_FLOW_DIRECTORY]: MutationHelpers.create,
  [types.EDIT_AUTOMATION_FLOW_DIRECTORY]: MutationHelpers.update,
  [types.DELETE_AUTOMATION_FLOW_DIRECTORY]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
